import {
	getAnswer,
	type OnboardingRecord,
} from '../../../onboarding/services/onboarding.service';
import GROWTHBOOK_FEATURE_FLAGS from '../../../../shared/utils/growthbook.util';

/**
 * Los dos experimentos del flujo de vinculación, con el MISMO contrato que
 * «phone-verification-optional» (ver `onboarding/constants/phoneOtpExperiment`):
 *
 * GrowthBook decide UNA sola vez, la primera vez que el usuario elegible abre
 * el flujo; la variante queda estampada en el record y de ahí en adelante TODO
 * lee el stamp, nunca GB. La asignación es pegajosa aunque la feature cambie o
 * el CDN se caiga, y la exposición queda limpia porque sólo se evalúa una vez
 * por usuario.
 *
 * Detener un experimento en GrowthBook frena asignaciones NUEVAS; des-honrar
 * los brazos ya estampados sería un cambio de código sobre los consumidores de
 * estos `read*`.
 *
 * Quien no tiene record de onboarding (17.5% de los que llegan al paso 1) no se
 * puede estampar: para esos se evalúa GB en vivo cada vez. El brazo sale igual
 * porque el hash es determinístico sobre `user_id`; lo único que pierden es la
 * congelación contra cambios de configuración.
 */
export type ExperimentArm = 'treatment' | 'control';

export const LINKING_ENTRY_FLAG =
	GROWTHBOOK_FEATURE_FLAGS.LINKING_ENTRY_DIRECT_METHOD;
export const LINKING_RESCUE_FLAG =
	GROWTHBOOK_FEATURE_FLAGS.LINKING_SUPPORT_RESCUE_THRESHOLD;

export const LINKING_ENTRY_ANSWER_KEY = 'linking.entry_arm';
export const LINKING_RESCUE_ANSWER_KEY = 'linking.rescue_arm';

const readArm = (
	record: OnboardingRecord | null | undefined,
	key: string,
): ExperimentArm | null => {
	const stamp = getAnswer<{ variant?: string }>(record?.answers ?? {}, key);
	return stamp?.variant === 'treatment' || stamp?.variant === 'control'
		? stamp.variant
		: null;
};

/** Brazo estampado del experimento de entrada, o null si no está asignado. */
export const readLinkingEntryArm = (
	record: OnboardingRecord | null | undefined,
) => readArm(record, LINKING_ENTRY_ANSWER_KEY);

/** Brazo estampado del experimento del rescate a soporte, o null. */
export const readLinkingRescueArm = (
	record: OnboardingRecord | null | undefined,
) => readArm(record, LINKING_RESCUE_ANSWER_KEY);

/**
 * `device` viaja como metadato de la PRIMERA exposición, no como parte del
 * brazo: el método concreto se re-deriva del dispositivo en uso cada vez. Quien
 * fue asignado desde el celular y vuelve en desktop debe recibir e.firma, que es
 * la hipótesis del experimento; congelar el método lo rompería.
 */
export const linkingEntryStampAnswer = (
	variant: ExperimentArm,
	device: 'mobile' | 'desktop',
) => ({
	[LINKING_ENTRY_ANSWER_KEY]: {
		label: 'Experimento: entrada directa a vinculación',
		value: { variant, device },
	},
});

export const linkingRescueStampAnswer = (
	variant: ExperimentArm,
	threshold: number,
) => ({
	[LINKING_RESCUE_ANSWER_KEY]: {
		label: 'Experimento: umbral de rescate a soporte',
		value: { variant, threshold },
	},
});
